import { useState } from 'react'
import { Dialog } from '@headlessui/react'
import { X } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { CheckCircle2, Circle, Filter, Info } from 'lucide-react'
import { callsAPI, ticketsAPI } from '../services/api'

function Tickets() {
  const [statusTab, setStatusTab] = useState('active')
  const [priority, setPriority] = useState('')
  const [category, setCategory] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const queryClient = useQueryClient()
  const [inspectTicket, setInspectTicket] = useState(null)

  const params = {
    page,
    limit: 20,
    ...(statusTab === 'active' ? { status: 'open' } : { status: 'closed' }),
    ...(priority && { priority }),
    ...(category && { category }),
    ...(search && { search })
  }

  const { data, isLoading } = useQuery(['tickets', params], () => ticketsAPI.list(params), { keepPreviousData: true })

  const closeMutation = useMutation((id) => ticketsAPI.close(id), {
    onSuccess: () => {
      toast.success('Ticket closed')
      queryClient.invalidateQueries('tickets')
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to close ticket')
  })

  const reopenMutation = useMutation((id) => ticketsAPI.reopen(id), {
    onSuccess: () => {
      toast.success('Ticket reopened')
      queryClient.invalidateQueries('tickets')
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to reopen ticket')
  })

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-900">Tickets</h1>
          <p className="mt-1 text-sm text-primary-600">Track and manage support tickets</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button className={`btn-secondary ${statusTab==='active'?'bg-primary-100':''}`} onClick={()=>{setStatusTab('active'); setPage(1)}}>Active</button>
            <button className={`btn-secondary ${statusTab==='resolved'?'bg-primary-100':''}`} onClick={()=>{setStatusTab('resolved'); setPage(1)}}>Resolved</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <input className="input-field" placeholder="Search" value={search} onChange={(e)=>{setSearch(e.target.value); setPage(1)}} />
            <select className="input-field" value={priority} onChange={(e)=>{setPriority(e.target.value); setPage(1)}}>
              <option value="">All priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <input className="input-field" placeholder="Category" value={category} onChange={(e)=>{setCategory(e.target.value); setPage(1)}} />
            <button className="btn-secondary" onClick={()=>{ setSearch(''); setPriority(''); setCategory(''); setPage(1) }}>
              <Filter className="h-4 w-4 mr-2" /> Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-primary-200">
            <thead className="bg-primary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-primary-200">
              {data?.data?.tickets?.map((t) => (
                <tr key={t._id}>
                  <td className="px-6 py-4 text-sm text-primary-900">{t.title}</td>
                  <td className="px-6 py-4 text-sm text-primary-900">{t.category}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`status-badge ${
                      t.priority === 'critical' ? 'bg-red-100 text-red-800' :
                      t.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      t.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {t.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-primary-900 capitalize">{t.status.replace('_',' ')}</td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button className="btn-secondary" onClick={()=>setInspectTicket(t)}> 
                      <Info className="h-4 w-4 mr-2"/> Details
                    </button>
                    {t.status !== 'closed' ? (
                      <button className="btn-secondary" onClick={()=>closeMutation.mutate(t._id)} disabled={closeMutation.isLoading}>
                        <CheckCircle2 className="h-4 w-4 mr-2"/> Close
                      </button>
                    ) : (
                      <button className="btn-secondary" onClick={()=>reopenMutation.mutate(t._id)} disabled={reopenMutation.isLoading}>
                        <Circle className="h-4 w-4 mr-2"/> Reopen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Ticket Details Modal */}
      {inspectTicket && (
        <TicketDetails ticket={inspectTicket} onClose={()=>setInspectTicket(null)} />
      )}
    </div>
  )
}

export default Tickets

function TicketDetails({ ticket, onClose }) {
  const callId = ticket?.callId && typeof ticket.callId === 'object' ? ticket.callId._id : ticket?.callId
  const { data: callData } = useQuery(
    ['call-details-for-ticket', callId],
    () => callsAPI.getCall(callId),
    { enabled: !!callId }
  )

  const { data: transcriptData } = useQuery(
    ['call-transcript-for-ticket', callId],
    () => callsAPI.getTranscript(callId),
    { enabled: !!callId }
  )

  const call = callData?.data?.call

  return (
    <Dialog open={!!ticket} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-primary-500/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl rounded bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold text-primary-900">Ticket Details</Dialog.Title>
            <button className="text-primary-500 hover:text-primary-700" onClick={onClose}><X className="h-5 w-5"/></button>
          </div>

          <div className="space-y-4">
            <div>
              <div className="text-sm text-primary-500">Title</div>
              <div className="text-sm text-primary-900">{ticket.title}</div>
            </div>
            <div>
              <div className="text-sm text-primary-500">Priority</div>
              <div className="text-sm capitalize">{ticket.priority}</div>
            </div>
            <div>
              <div className="text-sm text-primary-500">Category</div>
              <div className="text-sm">{ticket.category}</div>
            </div>

            {call && (
              <div className="border-t pt-4 space-y-3">
                <div className="text-sm font-semibold text-primary-900">Call Details</div>
                <div className="text-sm text-primary-700">Phone: {call.phoneNumber}</div>
                <div className="text-sm text-primary-700">From: {call.from} | To: {call.to}</div>
                <div className="text-sm text-primary-700 capitalize">Direction: {call.direction}</div>
                {transcriptData?.data?.call?.summary ? (
                  <div>
                    <div className="text-sm font-medium text-primary-900">Summary</div>
                    <div className="text-sm text-primary-700 whitespace-pre-wrap bg-primary-50 rounded p-3">{transcriptData.data.call.summary}</div>
                  </div>
                ) : call.summary ? (
                  <div>
                    <div className="text-sm font-medium text-primary-900">Summary</div>
                    <div className="text-sm text-primary-700 whitespace-pre-wrap bg-primary-50 rounded p-3">{call.summary}</div>
                  </div>
                ) : null}

                {transcriptData?.data?.call?.transcript ? (
                  <div>
                    <div className="text-sm font-medium text-primary-900">Transcript</div>
                    <div className="max-h-40 overflow-auto text-sm text-primary-700 whitespace-pre-wrap bg-primary-50 rounded p-3">{transcriptData.data.call.transcript}</div>
                  </div>
                ) : call.transcript ? (
                  <div>
                    <div className="text-sm font-medium text-primary-900">Transcript</div>
                    <div className="max-h-40 overflow-auto text-sm text-primary-700 whitespace-pre-wrap bg-primary-50 rounded p-3">{call.transcript}</div>
                  </div>
                ) : null}
                <div>
                  <div className="text-sm font-medium text-primary-900">Recording</div>
                  <audio controls className="w-full mt-2">
                    <source src={transcriptData?.data?.call?.recordingUrl || `${import.meta.env.VITE_API_URL || '/api'}/calls/${call._id}/audio`} />
                  </audio>
                </div>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}


